/*
 * WorkAndBuhin.vue の部品一覧表示、絞り込み、表示価格、無料部品表示の組み立てを切り出した処理。
 */
import { BuhinCategory, BuhinDetail, SubBuhinCategory } from "@/model/Buhin";
import { isJunseiBuhinCode } from "@/components/work-buhin/WorkBuhinMatch";
import { INSPECT_TYPE } from "@/model/InspectType";

export interface WorkBuhinDisplayMergeResult {
  buhinContent: SubBuhinCategory[];
  selFilter: string[];
  firstBuhinCd: string | null;
}

export interface WorkBuhinFilterActionState {
  selFilter: string[];
  buhinContent: SubBuhinCategory[];
}

export interface WorkBuhinFilterApplyEffectState {
  selFilter: string[];
  buhinContent: SubBuhinCategory[];
  shouldApplyBuhinContent: boolean;
}

export interface WorkBuhinSelectedDisplayState {
  buhinContent: SubBuhinCategory[];
  selFilter: string[];
  firstBuhinCd: string | null;
}

export interface SelectedBuhinMapActionState {
  shouldContinue: boolean;
  shouldResetFirstBuhinCd: boolean;
  shouldApplySelectedBuhin: boolean;
  shouldSetFilterBuhin: boolean;
  firstBuhinCd: string | null;
  buhinContent: SubBuhinCategory[];
  selFilter: string[];
}

export interface SelectedBuhinMapApplyEffectState {
  shouldContinue: boolean;
  shouldResetFirstBuhinCd: boolean;
  resetFirstBuhinCd: string | null;
  shouldApplySelectedBuhin: boolean;
  firstBuhinCd: string | null;
  buhinContent: SubBuhinCategory[];
  shouldSetFilterBuhin: boolean;
  selFilter: string[];
  shouldRunDelayedMap: boolean;
}

export interface SelectedBuhinMapSourceState {
  shouldResolveSelectedWork: boolean;
  buhinContentSource: SubBuhinCategory[];
}

export interface WorkBuhinUnselectedDisplayState {
  activeCateBuhins: SubBuhinCategory[];
  buhinContent: SubBuhinCategory[];
}

export interface UnselectedBuhinMapDelayedActionState {
  shouldApply: boolean;
  buhinCategory: BuhinCategory[];
  activeCateBuhins: SubBuhinCategory[];
  buhinContent: SubBuhinCategory[];
  shouldSetFilterBuhin: boolean;
  selFilter: string[];
}

export interface UnselectedBuhinMapDelayedSourceState {
  shouldBuildDelayedMap: boolean;
  latestBuhinCategorySource: BuhinCategory[];
  shouldResolveWork: boolean;
}

export interface UnselectedBuhinMapDelayedSourceApplyEffectState {
  shouldBuildDelayedMap: boolean;
  latestBuhinCategorySource: BuhinCategory[];
  shouldResolveWork: boolean;
}

export interface UnselectedBuhinMapDelayedApplyEffectState {
  shouldApply: boolean;
  buhinCategory: BuhinCategory[];
  activeCateBuhins: SubBuhinCategory[];
  buhinContent: SubBuhinCategory[];
  shouldSetFilterBuhin: boolean;
  selFilter: string[];
}

export interface WorkBuhinFilterState {
  selFilter: string[];
  shouldRefreshFilter: boolean;
}

export interface BuhinFilterApplyActionState {
  selFilter: string[];
  shouldApplyBuhinContent: boolean;
  buhinContent: SubBuhinCategory[];
}

export interface BuhinFilterApplyEffectState {
  selFilter: string[];
  shouldApplyBuhinContent: boolean;
  buhinContent: SubBuhinCategory[];
}

export interface ActiveBuhinCategorySourceState {
  sourceBuhinContent: SubBuhinCategory[];
}

export interface BuhinTopicDisplayState {
  shouldShowManualBuhinHeader: boolean;
  shouldShowBuhinTopic: boolean;
}

export interface BuhinPriceDisplayState {
  shouldDisplayDiscountPrice: boolean;
}

export interface BuhinFilterActiveState {
  isActive: boolean;
}

export interface BuhinScrollTargetState {
  shouldFindTarget: boolean;
  shouldUseManualHeader: boolean;
  selector: string;
  elementId: string;
  shouldScroll: boolean;
}

export interface BuhinScrollElementLookupState {
  shouldGetElementById: boolean;
  shouldQuerySelector: boolean;
  elementId: string;
  selector: string;
}

export interface BuhinScrollRetryActionState {
  shouldScrollIntoView: boolean;
  nRetryScroll: number;
  shouldRetry: boolean;
  retryTimeout: number;
}

export interface BuhinScrollRetryApplyEffectState {
  shouldScrollIntoView: boolean;
  nRetryScroll: number;
  shouldRetry: boolean;
  retryTimeout: number;
}

export interface BuhinBottomScrollTargetState {
  shouldFindElement: boolean;
  elementId: string;
  delay: number;
}

export interface BuhinBottomScrollElementLookupState {
  shouldGetElementById: boolean;
  elementId: string;
}

export interface BuhinBottomScrollActionState {
  shouldScrollToBottom: boolean;
  scrollTop: number;
}

export interface BuhinBottomScrollActionApplyEffectState {
  shouldScrollToBottom: boolean;
  scrollTop: number;
}

export interface BuhinImageDisplayState {
  hasImage: boolean;
}

export interface ExtendsImageButtonState {
  shouldShow: boolean;
  isDisabled: boolean;
  buttonClass: string;
  label: string;
}

export interface ManualBuhinListDisplayState {
  shouldShowList: boolean;
}

export interface ManualBuhinRowDisplayState {
  classes: {
    "edit-mode": boolean;
    "work-buhin-selected": boolean;
  };
  iconClasses: {
    "i-checked": boolean;
  };
  shouldShowNameText: boolean;
  shouldShowJunseiLabel: boolean;
  shouldShowEditButton: boolean;
  isEditButtonDisabled: boolean;
  editButtonLabel: string;
  shouldShowConfirmedStatus: boolean;
  shouldShowDiscountPrice: boolean;
}

export interface WorkBuhinTemplateDisplayState {
  shouldShowTitle: boolean;
  shouldShowManualWorkAdd: boolean;
  shouldShowBuhinError: boolean;
  shouldShowBuhin: boolean;
  shouldShowCloseButton: boolean;
}

export interface WorkBuhinLabelClassState {
  labelClass: string;
}

export interface WorkBuhinContainerDisplayState {
  workContainerClass: string;
  buhinContainerClass: string;
  shouldShowFooterButtons: boolean;
  shouldShowDialogButtons: boolean;
}

export interface RecommendWorkRowDisplayState {
  shouldShowRow: boolean;
  iconClasses: {
    "i-checked": boolean;
  };
}

export interface NormalBuhinRowDisplayState {
  isChecked: boolean;
  labelClasses: {
    "buhin-selected": boolean;
  };
  shouldShowExtremeLabel: boolean;
  shouldShowConfirmStatus: boolean;
  shouldShowDiscountPrice: boolean;
}

export interface WorkRowClassState {
  classes: {
    "work-buhin-selected": boolean;
    "item-active": boolean;
  };
}

export interface WorkRowActionDisplayState {
  shouldShowEditResultLink: boolean;
  shouldShowResetWorkLink: boolean;
}

export interface WorkRowLabelDisplayState {
  shouldShowDiscountLabel: boolean;
  shouldShowExtremeLabel: boolean;
  shouldShowNisseirenLabel: boolean;
  shouldShowManualWorkEditButton: boolean;
  shouldShowManualWorkNameText: boolean;
  shouldShowManualWorkEditInput: boolean;
  manualWorkEditButtonLabel: string;
  manualWorkNameSpan: number;
  nisseirenLabelSpan: number;
}

/*
 * mergeSelectedBuhinIntoContent を WorkAndBuhin.vue から分離し、
 * 部品一覧の表示用変換 を親側置換や単体テストで固定するため。
 */
export function mergeSelectedBuhinIntoContent(
  buhinContent: SubBuhinCategory[],
  selectedBuhinList: BuhinDetail[],
  activeBuhinShubetsuId: number
): WorkBuhinDisplayMergeResult {
  const selFilter: string[] = [];
  let firstBuhinCd: string | null = null;

  const mergedContent = buhinContent.map((group: SubBuhinCategory) => {
    const items = group.items.map((buhin: BuhinDetail) => {
      const displayBuhin = { ...buhin, buhinShubetsuId: activeBuhinShubetsuId };
      const selectedBuhin = findSelectedBuhinForDisplay(selectedBuhinList, displayBuhin);

      if (selectedBuhin) {
        if (firstBuhinCd == null && selectedBuhin.buhinCd) {
          firstBuhinCd = selectedBuhin.buhinCd;
        }
        selectedBuhin.isExtreme = displayBuhin.isExtreme;
        selectedBuhin.buhinShubetsuId = activeBuhinShubetsuId;
        if (selFilter.indexOf(group.buhinBunruiCd) == -1) {
          selFilter.push(group.buhinBunruiCd);
        }
        return {
          ...selectedBuhin,
          quantityStep: displayBuhin.quantityStep,
          defaultQuantity: displayBuhin.defaultQuantity,
          buhinShubetsuId: activeBuhinShubetsuId,
          buhinPriceZk: selectedBuhin.buhinPriceZk || displayBuhin.buhinPriceZk,
          buhinPriceZn: selectedBuhin.buhinPriceZn || displayBuhin.buhinPriceZn
        };
      }

      if (displayBuhin.quantity > 0) {
        return { ...displayBuhin, quantity: 0 };
      }
      return displayBuhin;
    });
    return { ...group, items };
  });

  return {
    buhinContent: mergedContent,
    selFilter,
    firstBuhinCd
  };
}

/*
 * findSelectedBuhinForDisplay を WorkAndBuhin.vue から分離し、
 * 部品一覧の表示用変換 を親側置換や単体テストで固定するため。
 */
export function findSelectedBuhinForDisplay(
  selectedBuhinList: BuhinDetail[],
  buhin: BuhinDetail
): BuhinDetail | undefined {
  return selectedBuhinList.find(
    (item: any) =>
      (item.buhinCd == buhin.buhinCd ||
        (item.buhinOsusumeId && item.buhinOsusumeId == buhin.buhinOsusumeId)) &&
      item.buhinNm == buhin.buhinNm &&
      item.isDelete != 1
  );
}

/*
 * filterOutBuhinByWorkName を WorkAndBuhin.vue から分離し、
 * 部品一覧の表示用変換 を親側置換や単体テストで固定するため。
 */
export function filterOutBuhinByWorkName(
  buhinContent: SubBuhinCategory[],
  seibiNm: string | null | undefined
): SubBuhinCategory[] {
  const filterOut = getFilterOutSide(seibiNm);
  if (!filterOut) {
    return buhinContent;
  }
  return buhinContent.map((cate: SubBuhinCategory) => {
    const items = cate.items.filter(
      (buhin: BuhinDetail) =>
        buhin.buhinNm.indexOf(filterOut) == -1 ||
        buhin.buhinNm.indexOf("左右") > -1
    );
    return { ...cate, items };
  });
}

/*
 * getFilterOutSide を WorkAndBuhin.vue から分離し、
 * 部品一覧の表示用変換 を親側置換や単体テストで固定するため。
 */
export function getFilterOutSide(seibiNm: string | null | undefined): string {
  if (!seibiNm || seibiNm.indexOf("左右") > -1) {
    return "";
  }
  if (seibiNm.indexOf("左") > -1) {
    return "右";
  }
  if (seibiNm.indexOf("右") > -1) {
    return "左";
  }
  return "";
}

/*
 * buildBuhinFilterSelection を WorkAndBuhin.vue から分離し、
 * 部品一覧の表示用変換 を親側置換や単体テストで固定するため。
 */
export function buildBuhinFilterSelection(
  listSelFilter: string[],
  activeBuhinCateIndex: number,
  defualtBuhinShubetsuIndex: number,
  defualtBuhinBunruiCdList: string[]
): string[] {
  if (listSelFilter.length > 0) {
    return listSelFilter;
  }
  if (activeBuhinCateIndex == defualtBuhinShubetsuIndex) {
    return [...defualtBuhinBunruiCdList];
  }
  return [];
}

/*
 * buildBuhinFilterState を WorkAndBuhin.vue から分離し、
 * filter state 反映と表示再構築の実行条件を親側置換や単体テストで固定するため。
 */
export function buildBuhinFilterState(
  listSelFilter: string[],
  activeBuhinCateIndex: number,
  defualtBuhinShubetsuIndex: number,
  defualtBuhinBunruiCdList: string[]
): WorkBuhinFilterState {
  return {
    selFilter: buildBuhinFilterSelection(
      listSelFilter,
      activeBuhinCateIndex,
      defualtBuhinShubetsuIndex,
      defualtBuhinBunruiCdList
    ),
    shouldRefreshFilter: true
  };
}

/*
 * buildBuhinFilterApplyActionState を WorkAndBuhin.vue から分離し、
 * setFilterBuhin 時の filter state 反映と表示棚再構築を一度に固定するため。
 */
export function buildBuhinFilterApplyActionState(params: {
  listSelFilter: string[];
  activeBuhinCateIndex: number;
  defualtBuhinShubetsuIndex: number;
  defualtBuhinBunruiCdList: string[];
  sourceBuhinContent: SubBuhinCategory[];
  activeCateBuhins: SubBuhinCategory[];
  selectedBuhinList: BuhinDetail[];
  freeOfChargeBuhinList: BuhinDetail[];
}): BuhinFilterApplyActionState {
  const filterState = buildBuhinFilterState(
    params.listSelFilter,
    params.activeBuhinCateIndex,
    params.defualtBuhinShubetsuIndex,
    params.defualtBuhinBunruiCdList
  );
  return {
    selFilter: filterState.selFilter,
    shouldApplyBuhinContent: filterState.shouldRefreshFilter,
    buhinContent: filterState.shouldRefreshFilter
      ? buildFilteredBuhinContent({
        selFilter: filterState.selFilter,
        sourceBuhinContent: params.sourceBuhinContent,
        activeCateBuhins: params.activeCateBuhins,
        selectedBuhinList: params.selectedBuhinList,
        freeOfChargeBuhinList: params.freeOfChargeBuhinList
      })
      : []
  };
}

/*
 * buildBuhinFilterApplyEffectState を WorkAndBuhin.vue から分離し、
 * setFilterBuhin で selFilter と buhinContent を component 側へ反映する条件と値を固定するため。
 */
export function buildBuhinFilterApplyEffectState(
  actionState: BuhinFilterApplyActionState
): BuhinFilterApplyEffectState {
  return {
    selFilter: actionState.selFilter,
    shouldApplyBuhinContent: actionState.shouldApplyBuhinContent,
    buhinContent: actionState.buhinContent
  };
}

/*
 * toggleBuhinFilterSelection を WorkAndBuhin.vue から分離し、
 * 部品一覧の表示用変換 を親側置換や単体テストで固定するため。
 */
export function toggleBuhinFilterSelection(
  selFilter: string[],
  buhinBunruiCd: string
): string[] {
  let nextFilter = [...selFilter];
  if (buhinBunruiCd != "") {
    if (nextFilter.findIndex((cd: string) => cd == buhinBunruiCd) == -1) {
      nextFilter.push(buhinBunruiCd);
    } else {
      nextFilter = nextFilter.filter((cd: string) => cd != buhinBunruiCd);
    }
  }
  return nextFilter.length > 0 ? nextFilter.sort() : nextFilter;
}

/*
 * buildFilteredBuhinContent を WorkAndBuhin.vue から分離し、
 * 部品一覧の表示用変換 を親側置換や単体テストで固定するため。
 */
export function buildFilteredBuhinContent(params: {
  selFilter: string[];
  sourceBuhinContent: SubBuhinCategory[];
  activeCateBuhins: SubBuhinCategory[];
  selectedBuhinList: BuhinDetail[];
  freeOfChargeBuhinList: BuhinDetail[];
}): SubBuhinCategory[] {
  const buhinContent = params.selFilter.length != 0
    ? buildSelectedFilterBuhinContent(
      params.selFilter,
      params.sourceBuhinContent,
      params.selectedBuhinList
    )
    : buildAllFilterBuhinContent(
      params.activeCateBuhins,
      params.selectedBuhinList
    );
  return applyDiscountShowPriceToContent(
    buhinContent,
    params.freeOfChargeBuhinList
  );
}

/*
 * buildBuhinFilterActionState を WorkAndBuhin.vue から分離し、
 * フィルタ切替後に反映する state を親側置換や単体テストで固定するため。
 */
export function buildBuhinFilterActionState(params: {
  selFilter: string[];
  buhinBunruiCd: string;
  sourceBuhinContent: SubBuhinCategory[];
  activeCateBuhins: SubBuhinCategory[];
  selectedBuhinList: BuhinDetail[];
  freeOfChargeBuhinList: BuhinDetail[];
}): WorkBuhinFilterActionState {
  const selFilter = toggleBuhinFilterSelection(
    params.selFilter,
    params.buhinBunruiCd
  );
  const buhinContent = buildFilteredBuhinContent({
    selFilter,
    sourceBuhinContent: params.sourceBuhinContent,
    activeCateBuhins: params.activeCateBuhins,
    selectedBuhinList: params.selectedBuhinList,
    freeOfChargeBuhinList: params.freeOfChargeBuhinList
  });
  return {
    selFilter,
    buhinContent
  };
}

/*
 * buildWorkBuhinFilterApplyEffectState を WorkAndBuhin.vue から分離し、
 * フィルタ切替後に component が反映する選択 filter と表示棚更新条件を固定するため。
 */
export function buildWorkBuhinFilterApplyEffectState(
  actionState: WorkBuhinFilterActionState
): WorkBuhinFilterApplyEffectState {
  return {
    selFilter: actionState.selFilter,
    buhinContent: actionState.buhinContent,
    shouldApplyBuhinContent: true
  };
}

/*
 * buildSelectedBuhinDisplayState を WorkAndBuhin.vue から分離し、
 * 選択済み部品を表示棚へ反映する順序を親側置換や単体テストで固定するため。
 */
export function buildSelectedBuhinDisplayState(params: {
  buhinContent: SubBuhinCategory[];
  selectedBuhinList: BuhinDetail[];
  activeBuhinShubetsuId: number;
  freeOfChargeBuhinList: BuhinDetail[];
  seibiNm: string | null | undefined;
}): WorkBuhinSelectedDisplayState {
  let buhinContent = filterOutBuhinByWorkName(
    params.buhinContent,
    params.seibiNm
  );
  buhinContent = applyDiscountShowPriceToContent(
    buhinContent,
    params.freeOfChargeBuhinList
  );
  return mergeSelectedBuhinIntoContent(
    buhinContent,
    params.selectedBuhinList,
    params.activeBuhinShubetsuId
  );
}

/*
 * buildSelectedBuhinMapSourceState を WorkAndBuhin.vue から分離し、
 * mapBuhin で選択中作業と部品棚ソースを参照する条件を固定するため。
 */
export function buildSelectedBuhinMapSourceState(params: {
  hasBuhinData: boolean;
  selectedBuhinLength: number;
  activeBuhinCateIndex: number;
  buhinCategory: BuhinCategory[];
}): SelectedBuhinMapSourceState {
  const shouldResolveSelectedWork =
    params.hasBuhinData && params.selectedBuhinLength > 0;
  const activeCategory = params.buhinCategory[params.activeBuhinCateIndex];
  return {
    shouldResolveSelectedWork,
    buhinContentSource:
      shouldResolveSelectedWork && activeCategory && activeCategory.items
        ? activeCategory.items
        : []
  };
}

/*
 * buildSelectedBuhinMapActionState を WorkAndBuhin.vue から分離し、
 * 選択済み部品がある場合の mapBuhin state 反映値と後続 filter 反映要否を固定するため。
 */
export function buildSelectedBuhinMapActionState(params: {
  hasBuhinData: boolean;
  buhinContent: SubBuhinCategory[];
  selectedBuhinList: BuhinDetail[];
  activeBuhinShubetsuId: number;
  freeOfChargeBuhinList: BuhinDetail[];
  seibiNm: string | null | undefined;
}): SelectedBuhinMapActionState {
  if (!params.hasBuhinData) {
    return {
      shouldContinue: false,
      shouldResetFirstBuhinCd: false,
      shouldApplySelectedBuhin: false,
      shouldSetFilterBuhin: false,
      firstBuhinCd: null,
      buhinContent: [],
      selFilter: []
    };
  }
  if (params.selectedBuhinList.length === 0) {
    return {
      shouldContinue: true,
      shouldResetFirstBuhinCd: true,
      shouldApplySelectedBuhin: false,
      shouldSetFilterBuhin: false,
      firstBuhinCd: null,
      buhinContent: [],
      selFilter: []
    };
  }
  const displayState = buildSelectedBuhinDisplayState({
    buhinContent: params.buhinContent,
    selectedBuhinList: params.selectedBuhinList,
    activeBuhinShubetsuId: params.activeBuhinShubetsuId,
    freeOfChargeBuhinList: params.freeOfChargeBuhinList,
    seibiNm: params.seibiNm
  });
  return {
    shouldContinue: true,
    shouldResetFirstBuhinCd: true,
    shouldApplySelectedBuhin: true,
    shouldSetFilterBuhin: true,
    firstBuhinCd: displayState.firstBuhinCd,
    buhinContent: displayState.buhinContent,
    selFilter: displayState.selFilter
  };
}

/*
 * buildSelectedBuhinMapApplyEffectState を WorkAndBuhin.vue から分離し、
 * mapBuhin の選択済み分岐で firstBuhinCd、buhinContent、filter を反映する条件を固定するため。
 */
export function buildSelectedBuhinMapApplyEffectState(
  actionState: SelectedBuhinMapActionState
): SelectedBuhinMapApplyEffectState {
  return {
    shouldContinue: actionState.shouldContinue,
    shouldResetFirstBuhinCd: actionState.shouldResetFirstBuhinCd,
    resetFirstBuhinCd: null,
    shouldApplySelectedBuhin: actionState.shouldApplySelectedBuhin,
    firstBuhinCd: actionState.firstBuhinCd,
    buhinContent: actionState.buhinContent,
    shouldSetFilterBuhin: actionState.shouldSetFilterBuhin,
    selFilter: actionState.selFilter,
    shouldRunDelayedMap: actionState.shouldContinue && !actionState.shouldApplySelectedBuhin
  };
}

/*
 * buildUnselectedBuhinDisplayState を WorkAndBuhin.vue から分離し、
 * 未選択時の表示棚再構築順序を親側置換や単体テストで固定するため。
 */
export function buildUnselectedBuhinDisplayState(params: {
  buhinContent: SubBuhinCategory[];
  freeOfChargeBuhinList: BuhinDetail[];
  seibiNm: string | null | undefined;
}): WorkBuhinUnselectedDisplayState {
  const activeCateBuhins = applyDiscountShowPriceToContent(
    params.buhinContent,
    params.freeOfChargeBuhinList
  );
  const buhinContent = filterOutBuhinByWorkName(
    activeCateBuhins,
    params.seibiNm
  );
  return {
    activeCateBuhins,
    buhinContent
  };
}

/*
 * buildUnselectedBuhinMapDelayedSourceState を WorkAndBuhin.vue から分離し、
 * 未選択時の遅延 mapBuhin で最新部品カテゴリと作業名を参照する条件を固定するため。
 */
export function buildUnselectedBuhinMapDelayedSourceState(params: {
  listBuhinData: Array<{ items: BuhinCategory[] }>;
}): UnselectedBuhinMapDelayedSourceState {
  const index = params.listBuhinData.length - 1;
  const latestBuhinData = index > -1 ? params.listBuhinData[index] : null;
  const latestBuhinCategorySource =
    latestBuhinData && latestBuhinData.items ? latestBuhinData.items : [];
  return {
    shouldBuildDelayedMap: latestBuhinCategorySource.length > 0,
    latestBuhinCategorySource,
    shouldResolveWork: latestBuhinCategorySource.length > 0
  };
}

/*
 * buildUnselectedBuhinMapDelayedSourceApplyEffectState を WorkAndBuhin.vue から分離し、
 * 未選択 delayed map で deepCopy と getWork を行う条件と参照元を固定するため。
 */
export function buildUnselectedBuhinMapDelayedSourceApplyEffectState(
  sourceState: UnselectedBuhinMapDelayedSourceState
): UnselectedBuhinMapDelayedSourceApplyEffectState {
  return {
    shouldBuildDelayedMap: sourceState.shouldBuildDelayedMap,
    latestBuhinCategorySource: sourceState.latestBuhinCategorySource,
    shouldResolveWork: sourceState.shouldResolveWork
  };
}

/*
 * buildUnselectedBuhinMapDelayedActionState を WorkAndBuhin.vue から分離し、
 * 未選択時の遅延 mapBuhin で表示棚を再構築する条件と反映値を固定するため。
 */
export function buildUnselectedBuhinMapDelayedActionState(params: {
  latestBuhinCategory: BuhinCategory[];
  activeBuhinCateIndex: number;
  freeOfChargeBuhinList: BuhinDetail[];
  seibiNm: string | null | undefined;
}): UnselectedBuhinMapDelayedActionState {
  const activeCategory = params.latestBuhinCategory[params.activeBuhinCateIndex];
  if (!activeCategory || !activeCategory.items) {
    return {
      shouldApply: false,
      buhinCategory: [],
      activeCateBuhins: [],
      buhinContent: [],
      shouldSetFilterBuhin: false,
      selFilter: []
    };
  }
  const displayState = buildUnselectedBuhinDisplayState({
    buhinContent: activeCategory.items,
    freeOfChargeBuhinList: params.freeOfChargeBuhinList,
    seibiNm: params.seibiNm
  });
  return {
    shouldApply: true,
    buhinCategory: params.latestBuhinCategory,
    activeCateBuhins: displayState.activeCateBuhins,
    buhinContent: displayState.buhinContent,
    shouldSetFilterBuhin: true,
    selFilter: []
  };
}

/*
 * buildUnselectedBuhinMapDelayedApplyEffectState を WorkAndBuhin.vue から分離し、
 * 未選択 delayed map の部品カテゴリ、表示棚、filter 反映条件を固定するため。
 */
export function buildUnselectedBuhinMapDelayedApplyEffectState(
  actionState: UnselectedBuhinMapDelayedActionState
): UnselectedBuhinMapDelayedApplyEffectState {
  return {
    shouldApply: actionState.shouldApply,
    buhinCategory: actionState.buhinCategory,
    activeCateBuhins: actionState.activeCateBuhins,
    buhinContent: actionState.buhinContent,
    shouldSetFilterBuhin: actionState.shouldSetFilterBuhin,
    selFilter: actionState.selFilter
  };
}

/*
 * buildActiveBuhinCategorySourceState を WorkAndBuhin.vue から分離し、
 * 部品絞り込みで利用する現在カテゴリの部品棚ソースを固定するため。
 */
export function buildActiveBuhinCategorySourceState(params: {
  buhinCategory: BuhinCategory[];
  activeBuhinCateIndex: number;
}): ActiveBuhinCategorySourceState {
  const activeCategory = params.buhinCategory[params.activeBuhinCateIndex];
  return {
    sourceBuhinContent:
      activeCategory && activeCategory.items
        ? activeCategory.items
        : []
  };
}

/*
 * buildSelectedFilterBuhinContent を WorkAndBuhin.vue から分離し、
 * 部品一覧の表示用変換 を親側置換や単体テストで固定するため。
 */
export function buildSelectedFilterBuhinContent(
  selFilter: string[],
  sourceBuhinContent: SubBuhinCategory[],
  selectedBuhinList: BuhinDetail[]
): SubBuhinCategory[] {
  const buhinContent: SubBuhinCategory[] = [];
  selFilter.forEach((filterCd: string) => {
    const filteredData = sourceBuhinContent.filter(
      (data: SubBuhinCategory) => data.buhinBunruiCd == filterCd
    );
    if (filteredData.length > 0) {
      const items = mergeSelectedBuhinPriceIntoItems(
        filteredData[0].items,
        selectedBuhinList,
        {
          includeIsDiscount: true,
          preserveNotFreeZeroPrice: true
        }
      );
      buhinContent.push({ ...filteredData[0], items });
    }
  });
  return buhinContent;
}

/*
 * buildAllFilterBuhinContent を WorkAndBuhin.vue から分離し、
 * 部品一覧の表示用変換 を親側置換や単体テストで固定するため。
 */
export function buildAllFilterBuhinContent(
  activeCateBuhins: SubBuhinCategory[],
  selectedBuhinList: BuhinDetail[]
): SubBuhinCategory[] {
  return activeCateBuhins.map((bunrui: SubBuhinCategory) => {
    const items = mergeSelectedBuhinPriceIntoItems(
      bunrui.items,
      selectedBuhinList
    );
    return { ...bunrui, items };
  });
}

/*
 * shouldDisplayDiscountPrice を WorkAndBuhin.vue から分離し、
 * 部品一覧の表示用変換 を親側置換や単体テストで固定するため。
 */
export function shouldDisplayDiscountPrice(
  buhin: BuhinDetail,
  isDiscount: boolean
): boolean {
  if (!isDiscount) {
    return false;
  }
  if (isJunseiBuhinCode(buhin.buhinCd) && buhin.canJunseiFree == 1) {
    return true;
  }
  if (Object.prototype.hasOwnProperty.call(buhin, "notFreeTanka") && buhin.notFreeTanka != 1) {
    return true;
  }
  return false;
}

/*
 * buildBuhinPriceDisplayState を WorkAndBuhin.vue から分離し、
 * 部品価格表示の判定結果を親側置換や単体テストで固定するため。
 */
export function buildBuhinPriceDisplayState(
  buhin: BuhinDetail,
  isDiscount: boolean
): BuhinPriceDisplayState {
  return {
    shouldDisplayDiscountPrice: shouldDisplayDiscountPrice(buhin, isDiscount)
  };
}

/*
 * isBuhinFilterActive を WorkAndBuhin.vue から分離し、
 * 部品一覧の表示用変換 を親側置換や単体テストで固定するため。
 */
export function isBuhinFilterActive(
  selFilter: string[],
  buhinBunruiCd: string
): boolean {
  return selFilter.some((cd: string) => cd == buhinBunruiCd);
}

/*
 * buildBuhinFilterActiveState を WorkAndBuhin.vue から分離し、
 * filter active 表示の判定結果を親側置換や単体テストで固定するため。
 */
export function buildBuhinFilterActiveState(
  selFilter: string[],
  buhinBunruiCd: string
): BuhinFilterActiveState {
  return {
    isActive: isBuhinFilterActive(selFilter, buhinBunruiCd)
  };
}

/*
 * buildBuhinScrollTargetState を WorkAndBuhin.vue から分離し、
 * 部品表示後にスクロールする DOM 対象の優先順と検索条件を固定するため。
 */
export function buildBuhinScrollTargetState(params: {
  firstBuhinCd: string | null | undefined;
  listManualBuhinFilteredLength: number;
}): BuhinScrollTargetState {
  if (params.firstBuhinCd) {
    return {
      shouldFindTarget: true,
      shouldUseManualHeader: false,
      selector: `[buhin-cd="${params.firstBuhinCd}"]`,
      elementId: "",
      shouldScroll: true
    };
  }
  if (params.listManualBuhinFilteredLength > 0) {
    return {
      shouldFindTarget: true,
      shouldUseManualHeader: true,
      selector: "",
      elementId: "topic-buhin-manual",
      shouldScroll: true
    };
  }
  return {
    shouldFindTarget: false,
    shouldUseManualHeader: false,
    selector: "",
    elementId: "",
    shouldScroll: false
  };
}

/*
 * buildBuhinScrollElementLookupState を WorkAndBuhin.vue から分離し、
 * 部品スクロール対象の DOM 検索方法を target state から固定するため。
 */
export function buildBuhinScrollElementLookupState(
  targetState: BuhinScrollTargetState
): BuhinScrollElementLookupState {
  if (!targetState.shouldFindTarget) {
    return {
      shouldGetElementById: false,
      shouldQuerySelector: false,
      elementId: "",
      selector: ""
    };
  }
  if (targetState.shouldUseManualHeader) {
    return {
      shouldGetElementById: true,
      shouldQuerySelector: false,
      elementId: targetState.elementId,
      selector: ""
    };
  }
  return {
    shouldGetElementById: false,
    shouldQuerySelector: true,
    elementId: "",
    selector: targetState.selector
  };
}

/*
 * buildBuhinScrollRetryActionState を WorkAndBuhin.vue から分離し、
 * スクロール対象の有無に応じた retry reset / retry 継続条件を固定するため。
 */
export function buildBuhinScrollRetryActionState(params: {
  hasElement: boolean;
  shouldScroll: boolean;
  nRetryScroll: number;
  retryLimit?: number;
  retryTimeout?: number;
}): BuhinScrollRetryActionState {
  const retryLimit = params.retryLimit == null ? 3 : params.retryLimit;
  const retryTimeout = params.retryTimeout == null ? 300 : params.retryTimeout;
  if (params.hasElement) {
    return {
      shouldScrollIntoView: true,
      nRetryScroll: 0,
      shouldRetry: false,
      retryTimeout
    };
  }
  if (params.shouldScroll && params.nRetryScroll < retryLimit) {
    return {
      shouldScrollIntoView: false,
      nRetryScroll: params.nRetryScroll + 1,
      shouldRetry: true,
      retryTimeout
    };
  }
  return {
    shouldScrollIntoView: false,
    nRetryScroll: params.nRetryScroll,
    shouldRetry: false,
    retryTimeout
  };
}

/*
 * buildBuhinScrollRetryApplyEffectState を WorkAndBuhin.vue から分離し、
 * scrollToBuhin の scroll 実行、retry 回数更新、再試行条件を component 側で固定して適用するため。
 */
export function buildBuhinScrollRetryApplyEffectState(
  retryState: BuhinScrollRetryActionState
): BuhinScrollRetryApplyEffectState {
  return {
    shouldScrollIntoView: retryState.shouldScrollIntoView,
    nRetryScroll: retryState.nRetryScroll,
    shouldRetry: retryState.shouldRetry,
    retryTimeout: retryState.retryTimeout
  };
}

/*
 * buildBuhinBottomScrollTargetState を WorkAndBuhin.vue から分離し、
 * 一覧末尾 scroll の対象 id と delay を DOM 操作前に固定するため。
 */
export function buildBuhinBottomScrollTargetState(
  elementId: string,
  delay: number = 50
): BuhinBottomScrollTargetState {
  return {
    shouldFindElement: !!elementId,
    elementId: elementId || "",
    delay
  };
}

/*
 * buildBuhinBottomScrollElementLookupState を WorkAndBuhin.vue から分離し、
 * 一覧末尾 scroll 対象の DOM 検索有無と id を target state から固定するため。
 */
export function buildBuhinBottomScrollElementLookupState(
  targetState: BuhinBottomScrollTargetState
): BuhinBottomScrollElementLookupState {
  return {
    shouldGetElementById: targetState.shouldFindElement,
    elementId: targetState.shouldFindElement ? targetState.elementId : ""
  };
}

/*
 * buildBuhinBottomScrollActionState を WorkAndBuhin.vue から分離し、
 * DOM element の有無に応じて末尾 scroll の実行条件と scrollTop を固定するため。
 */
export function buildBuhinBottomScrollActionState(params: {
  hasElement: boolean;
  scrollHeight: number;
}): BuhinBottomScrollActionState {
  if (!params.hasElement) {
    return {
      shouldScrollToBottom: false,
      scrollTop: 0
    };
  }
  return {
    shouldScrollToBottom: true,
    scrollTop: params.scrollHeight
  };
}

/*
 * buildBuhinBottomScrollActionApplyEffectState を WorkAndBuhin.vue から分離し、
 * 一覧末尾 scroll の DOM 副作用を実行する条件と scrollTop を親側置換や単体テストで固定するため。
 */
export function buildBuhinBottomScrollActionApplyEffectState(
  actionState: BuhinBottomScrollActionState
): BuhinBottomScrollActionApplyEffectState {
  return {
    shouldScrollToBottom: actionState.shouldScrollToBottom,
    scrollTop: actionState.scrollTop
  };
}

/*
 * shouldShowManualBuhinHeader を WorkAndBuhin.vue から分離し、
 * 部品一覧の表示用変換 を親側置換や単体テストで固定するため。
 */
export function shouldShowManualBuhinHeader(
  listManualBuhinFiltered: BuhinDetail[]
): boolean {
  return listManualBuhinFiltered.some(
    (buhin: BuhinDetail) => !isJunseiBuhinCode(buhin.buhinCd)
  );
}

/*
 * buildManualBuhinHeaderDisplayState を WorkAndBuhin.vue から分離し、
 * 手入力部品見出し表示の state 作成を親側置換や単体テストで固定するため。
 */
export function buildManualBuhinHeaderDisplayState(
  listManualBuhinFiltered: BuhinDetail[]
): Pick<BuhinTopicDisplayState, "shouldShowManualBuhinHeader"> {
  return {
    shouldShowManualBuhinHeader: shouldShowManualBuhinHeader(
      listManualBuhinFiltered
    )
  };
}

/*
 * shouldShowBuhinTopic を WorkAndBuhin.vue から分離し、
 * 部品一覧の表示用変換 を親側置換や単体テストで固定するため。
 */
export function shouldShowBuhinTopic(params: {
  target: SubBuhinCategory;
  index: number;
  buhinContentLength: number;
  listManualBuhinName: string;
  listManualBuhinFilteredLength: number;
}): boolean {
  if (params.index + 1 == params.buhinContentLength) {
    if (params.target.buhinBunruiNm == params.listManualBuhinName) {
      if (
        params.target.items &&
        params.target.items.length == 0 &&
        params.listManualBuhinFilteredLength > 0
      ) {
        return false;
      }
    }
  }
  return true;
}

/*
 * buildBuhinTopicDisplayState を WorkAndBuhin.vue から分離し、
 * 部品見出し表示の判定結果を親側置換や単体テストで固定するため。
 */
export function buildBuhinTopicDisplayState(params: {
  target: SubBuhinCategory;
  index: number;
  buhinContentLength: number;
  listManualBuhinName: string;
  listManualBuhinFiltered: BuhinDetail[];
}): BuhinTopicDisplayState {
  return {
    shouldShowManualBuhinHeader: shouldShowManualBuhinHeader(
      params.listManualBuhinFiltered
    ),
    shouldShowBuhinTopic: shouldShowBuhinTopic({
      target: params.target,
      index: params.index,
      buhinContentLength: params.buhinContentLength,
      listManualBuhinName: params.listManualBuhinName,
      listManualBuhinFilteredLength: params.listManualBuhinFiltered.length
    })
  };
}

/*
 * hasExtendsImageInspect を WorkAndBuhin.vue から分離し、
 * 部品一覧の表示用変換 を親側置換や単体テストで固定するため。
 */
export function hasExtendsImageInspect(params: {
  isCanExtendsImage: boolean;
  extendsImageInspect: any;
}): boolean {
  return !!(
    params.isCanExtendsImage &&
    params.extendsImageInspect &&
    params.extendsImageInspect.imageList &&
    params.extendsImageInspect.imageList.length > 0
  );
}

/*
 * buildBuhinImageDisplayState を WorkAndBuhin.vue から分離し、
 * 画像表示の判定結果を親側置換や単体テストで固定するため。
 */
export function buildBuhinImageDisplayState(params: {
  isCanExtendsImage: boolean;
  extendsImageInspect: any;
}): BuhinImageDisplayState {
  return {
    hasImage: hasExtendsImageInspect(params)
  };
}

/*
 * buildExtendsImageButtonState を WorkAndBuhin.vue から分離し、
 * 画像登録ボタンの表示・disabled・class・文言を同じ画像有無条件で固定するため。
 */
export function buildExtendsImageButtonState(params: {
  inspectResult: any;
  isCanExtendsImage: boolean;
  extendsImageInspect: any;
}): ExtendsImageButtonState {
  const hasImage = hasExtendsImageInspect({
    isCanExtendsImage: params.isCanExtendsImage,
    extendsImageInspect: params.extendsImageInspect
  });
  return {
    shouldShow: !!params.inspectResult && params.isCanExtendsImage,
    isDisabled: !params.extendsImageInspect,
    buttonClass: hasImage ? "green" : "extends-image-gray",
    label: hasImage ? "画像あり" : "画像登録"
  };
}

/*
 * buildManualBuhinListDisplayState を WorkAndBuhin.vue から分離し、
 * 手入力・純正部品一覧の表示有無を filter 済み list の件数で固定するため。
 */
export function buildManualBuhinListDisplayState(
  listManualBuhinFiltered: BuhinDetail[]
): ManualBuhinListDisplayState {
  return {
    shouldShowList: listManualBuhinFiltered.length > 0
  };
}

/*
 * buildManualBuhinRowDisplayState を WorkAndBuhin.vue から分離し、
 * 手入力・純正部品行の編集状態、選択状態、純正ラベル、編集ボタン可否を固定するため。
 */
export function buildManualBuhinRowDisplayState(buhin: BuhinDetail): ManualBuhinRowDisplayState {
  const displayBuhin = buhin as any;
  const isJunseiBuhin = isJunseiBuhinCode(buhin.buhinCd);
  return {
    classes: {
      "edit-mode": !!displayBuhin.editMode,
      "work-buhin-selected": buhin.quantity > 0
    },
    iconClasses: {
      "i-checked": buhin.quantity == 0
    },
    shouldShowNameText: !displayBuhin.editMode,
    shouldShowJunseiLabel: isJunseiBuhin,
    shouldShowEditButton: !isJunseiBuhin,
    isEditButtonDisabled: buhin.buhinNm == "",
    editButtonLabel: displayBuhin.editMode ? "保存" : "編集",
    shouldShowConfirmedStatus: !!displayBuhin.id,
    shouldShowDiscountPrice: !!displayBuhin.shouldDisplayDiscountPrice
  };
}

/*
 * buildWorkBuhinTemplateDisplayState を WorkAndBuhin.vue から分離し、
 * template 全体の単純な表示 boolean をまとめて固定するため。
 */
export function buildWorkBuhinTemplateDisplayState(params: {
  showTitle: boolean;
  editSeibi: boolean;
  isBuhinError: boolean;
  showBuhin: boolean;
  closeBtn: boolean;
}): WorkBuhinTemplateDisplayState {
  return {
    shouldShowTitle: params.showTitle,
    shouldShowManualWorkAdd: params.editSeibi,
    shouldShowBuhinError: params.isBuhinError,
    shouldShowBuhin: params.showBuhin,
    shouldShowCloseButton: params.closeBtn
  };
}

/*
 * buildWorkBuhinLabelClassState を WorkAndBuhin.vue から分離し、
 * dialog 表示有無で切り替わる作業・部品 label class を固定するため。
 */
export function buildWorkBuhinLabelClassState(params: {
  isDialog: boolean;
  baseClass: string;
}): WorkBuhinLabelClassState {
  return {
    labelClass: params.isDialog
      ? params.baseClass
      : `${params.baseClass} mini-label`
  };
}

/*
 * buildWorkBuhinContainerDisplayState を WorkAndBuhin.vue から分離し、
 * dialog 表示時の画面枠 class と footer / dialog ボタン表示条件を固定するため。
 */
export function buildWorkBuhinContainerDisplayState(params: {
  isDialog: boolean;
}): WorkBuhinContainerDisplayState {
  return {
    workContainerClass: params.isDialog ? "box-left" : "",
    buhinContainerClass: params.isDialog ? "dialog-mode" : "not-dialog-mode",
    shouldShowFooterButtons: !params.isDialog,
    shouldShowDialogButtons: params.isDialog
  };
}

/*
 * buildRecommendWorkRowDisplayState を WorkAndBuhin.vue から分離し、
 * おすすめ作業行の表示有無と選択 icon class 条件を固定するため。
 */
export function buildRecommendWorkRowDisplayState(data: any): RecommendWorkRowDisplayState {
  return {
    shouldShowRow: !!data.sknRecommendInspecSeibiNm,
    iconClasses: {
      "i-checked": data.buhinSelected == 0
    }
  };
}

/*
 * buildNormalBuhinRowDisplayState を WorkAndBuhin.vue から分離し、
 * 通常部品行の選択表示、Extreme label、確定済み表示、無償価格表示を固定するため。
 */
export function buildNormalBuhinRowDisplayState(params: {
  buhin: BuhinDetail;
  isSelected: boolean;
  isConfirmed: boolean;
  shouldDisplayDiscountPrice: boolean;
}): NormalBuhinRowDisplayState {
  return {
    isChecked: params.isSelected,
    labelClasses: {
      "buhin-selected": params.isSelected
    },
    shouldShowExtremeLabel: !!(
      (params.buhin as any).isExtreme &&
      (params.buhin as any).isExtreme != 0
    ),
    shouldShowConfirmStatus: params.isConfirmed,
    shouldShowDiscountPrice: params.shouldDisplayDiscountPrice
  };
}

/*
 * buildNormalWorkRowClassState を WorkAndBuhin.vue から分離し、
 * 通常作業行の選択済み表示と active 表示の class 条件を固定するため。
 */
export function buildNormalWorkRowClassState(params: {
  work: any;
  index: number;
  activeWorkIndex: number;
  isManualWork: boolean;
}): WorkRowClassState {
  return {
    classes: {
      "work-buhin-selected":
        params.work.buhinSelected > 0 || params.work.discountWorkSelected > 0,
      "item-active": params.activeWorkIndex == params.index && params.isManualWork == false
    }
  };
}

/*
 * buildManualWorkRowClassState を WorkAndBuhin.vue から分離し、
 * 手入力作業行の選択済み表示と active 表示の class 条件を固定するため。
 */
export function buildManualWorkRowClassState(params: {
  work: any;
  index: number;
  activeWorkIndex: number;
  isManualWork: boolean;
}): WorkRowClassState {
  return {
    classes: {
      "work-buhin-selected": params.work.buhinSelected > 0,
      "item-active": params.activeWorkIndex == params.index && params.isManualWork == true
    }
  };
}

/*
 * buildWorkRowActionDisplayState を WorkAndBuhin.vue から分離し、
 * 編集リンクと削除リンクの作業種別・保存可否・選択済み条件を固定するため。
 */
export function buildWorkRowActionDisplayState(params: {
  editResult: boolean;
  work: any;
}): WorkRowActionDisplayState {
  const isEditResultWork = isEditResultInspectType(params.work.sknInspectType);
  return {
    shouldShowEditResultLink: params.editResult && isEditResultWork,
    shouldShowResetWorkLink:
      (params.work.buhinSelected > 0 || params.work.discountWorkSelected > 0) &&
      !params.work.disabledSave &&
      !isEditResultWork
  };
}

/*
 * buildWorkRowLabelDisplayState を WorkAndBuhin.vue から分離し、
 * 作業行ラベル表示、手入力作業 editMode 表示、日整連作業の列幅・編集可否条件を固定するため。
 */
export function buildWorkRowLabelDisplayState(work: any): WorkRowLabelDisplayState {
  const hasNisseirenWork = !!work.sknNisseirenCd;
  const isEditMode = !!work.editMode;
  return {
    shouldShowDiscountLabel: work.isDiscount === 1,
    shouldShowExtremeLabel: !!(work.isExtreme && work.isExtreme != 0),
    shouldShowNisseirenLabel: hasNisseirenWork,
    shouldShowManualWorkEditButton: !hasNisseirenWork,
    shouldShowManualWorkNameText: !isEditMode,
    shouldShowManualWorkEditInput: isEditMode,
    manualWorkEditButtonLabel: isEditMode ? "保存" : "編集",
    manualWorkNameSpan: hasNisseirenWork ? 12 : 13,
    nisseirenLabelSpan: hasNisseirenWork ? 3 : 2
  };
}

/*
 * isEditResultInspectType を WorkAndBuhin.vue から分離し、
 * 実績編集リンク対象の点検種別を定数ベースで固定するため。
 */
export function isEditResultInspectType(sknInspectType: any): boolean {
  return sknInspectType == INSPECT_TYPE.RECOMMEND ||
    sknInspectType == INSPECT_TYPE.RECOMMEND_MANUAL;
}

/*
 * applyDiscountShowPriceToContent を WorkAndBuhin.vue から分離し、
 * 部品一覧の表示用変換 を親側置換や単体テストで固定するため。
 */
export function applyDiscountShowPriceToContent(
  buhinContent: SubBuhinCategory[],
  freeOfChargeBuhinList: BuhinDetail[]
): SubBuhinCategory[] {
  return buhinContent.map((element: SubBuhinCategory) => {
    const items = element.items.map((buhin: BuhinDetail) =>
      applyDiscountShowPriceToBuhin(buhin, freeOfChargeBuhinList)
    );
    return { ...element, items };
  });
}

/*
 * applyDiscountShowPriceToBuhin を WorkAndBuhin.vue から分離し、
 * 部品一覧の表示用変換 を親側置換や単体テストで固定するため。
 */
export function applyDiscountShowPriceToBuhin(
  buhin: BuhinDetail,
  freeOfChargeBuhinList: BuhinDetail[]
): BuhinDetail {
  if (buhin.notFreeTanka === 1) {
    return { ...buhin, isDiscount: 1 } as any;
  }
  if (isFreeOfChargeBuhin(freeOfChargeBuhinList, buhin)) {
    return {
      ...buhin,
      kingakuZn: 0,
      kingakuZk: 0,
      buhinPriceZn: 0,
      buhinPriceZk: 0,
      isDiscount: 1
    } as any;
  }
  return { ...buhin, isDiscount: 0 } as any;
}

/*
 * isFreeOfChargeBuhin を WorkAndBuhin.vue から分離し、
 * 部品一覧の表示用変換 を親側置換や単体テストで固定するため。
 */
export function isFreeOfChargeBuhin(
  freeOfChargeBuhinList: BuhinDetail[],
  buhin: BuhinDetail
): boolean {
  return freeOfChargeBuhinList.some(
    (freeBuhin: BuhinDetail) =>
      freeBuhin.buhinCd != null &&
      buhin.buhinCd != null &&
      freeBuhin.buhinCd.toString() === buhin.buhinCd.toString()
  );
}

export interface MergeSelectedBuhinPriceOptions {
  includeIsDiscount?: boolean;
  preserveNotFreeZeroPrice?: boolean;
}

/*
 * mergeSelectedBuhinPriceIntoItems を WorkAndBuhin.vue から分離し、
 * 部品一覧の表示用変換 を親側置換や単体テストで固定するため。
 */
export function mergeSelectedBuhinPriceIntoItems(
  items: BuhinDetail[],
  selectedBuhinList: BuhinDetail[],
  options: MergeSelectedBuhinPriceOptions = {}
): BuhinDetail[] {
  return items.map((buhin: BuhinDetail) => {
    const selectedBuhin = selectedBuhinList.find(
      (selected: BuhinDetail) => selected.buhinCd == buhin.buhinCd
    );
    if (!selectedBuhin) {
      return { ...buhin };
    }
    return mergeSelectedBuhinPrice(buhin, selectedBuhin, options);
  });
}

/*
 * mergeSelectedBuhinPrice を WorkAndBuhin.vue から分離し、
 * 部品一覧の表示用変換 を親側置換や単体テストで固定するため。
 */
export function mergeSelectedBuhinPrice(
  buhin: BuhinDetail,
  selectedBuhin: BuhinDetail,
  options: MergeSelectedBuhinPriceOptions = {}
): BuhinDetail {
  if (
    options.preserveNotFreeZeroPrice &&
    buhin.notFreeTanka === 1 &&
    selectedBuhin.buhinPriceZk == 0 &&
    selectedBuhin.buhinPriceZn == 0
  ) {
    return {
      ...buhin,
      quantity: selectedBuhin.quantity,
      isDiscount: (selectedBuhin as any).isDiscount
    } as any;
  }

  const merged: any = {
    ...buhin,
    buhinPriceZk: selectedBuhin.buhinPriceZk,
    buhinPriceZn: selectedBuhin.buhinPriceZn,
    quantity: selectedBuhin.quantity
  };
  if (options.includeIsDiscount) {
    merged.isDiscount = (selectedBuhin as any).isDiscount;
  }
  return merged;
}
